import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, BadgeCheck, CreditCard, Star, Users, Flag, Phone } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stars } from "@/components/Stars";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <Icon className="h-5 w-5 text-[#1E40AF]" />
    <p className="mt-3 font-display text-3xl font-extrabold">{value ?? "–"}</p>
    <p className="text-sm text-slate-500">{label}</p>
  </div>
);

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [claimed, setClaimed] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [phones, setPhones] = useState([]);
  const load = () => {
    api.get("/admin/phone-suggestions").then((r) => setPhones(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/claimed").then((r) => setClaimed(r.data)).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="admin-page">
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Administration</h1>
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-7">
        <Kpi icon={Building2} label="Entreprises en base" value={stats?.businesses} testid="kpi-businesses" />
        <Kpi icon={BadgeCheck} label="Fiches revendiquées" value={stats?.claimed} testid="kpi-claimed" />
        <Kpi icon={CreditCard} label="Abonnements actifs" value={stats?.subscriptions} testid="kpi-subscriptions" />
        <Kpi icon={Star} label="Avis" value={stats?.reviews} testid="kpi-reviews" />
        <Kpi icon={Flag} label="Avis signalés" value={stats?.reported} testid="kpi-reported" />
        <Kpi icon={Phone} label="Numéros à valider" value={stats?.pending_phones} testid="kpi-pending-phones" />
        <Kpi icon={Users} label="Utilisateurs" value={stats?.users} testid="kpi-users" />
      </div>
      <Tabs defaultValue="claimed" className="mt-10">
        <TabsList><TabsTrigger value="claimed" data-testid="admin-tab-claimed">Fiches revendiquées</TabsTrigger><TabsTrigger value="reviews" data-testid="admin-tab-reviews">Avis{stats?.reported ? ` (${stats.reported} signalé${stats.reported > 1 ? "s" : ""})` : ""}</TabsTrigger><TabsTrigger value="phones" data-testid="admin-tab-phones">Numéros proposés{phones.length ? ` (${phones.length})` : ""}</TabsTrigger></TabsList>
        <TabsContent value="phones" className="mt-4 space-y-2">
          {phones.length === 0 && <p className="text-sm text-slate-500">Aucun numéro en attente.</p>}
          {phones.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-phone-${p.id}`}>
              <div>
                <Link to={`/entreprise/${p.business_id}`} className="font-semibold hover:text-[#1E40AF]">{p.business_name}</Link>
                <p className="font-mono-id text-lg">{p.phone}</p>
                <p className="text-xs text-slate-500">Proposé par {p.author} ({p.author_email}) le {new Date(p.created_at).toLocaleDateString("fr-FR")}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="bg-[#166534] hover:bg-[#14532D] hover:text-white" onClick={() => act(() => api.post(`/admin/phone-suggestions/${p.id}/approve`), "Numéro validé et affiché")} data-testid={`admin-phone-approve-${p.id}`}>Valider</Button>
                <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/phone-suggestions/${p.id}/reject`), "Proposition refusée")} data-testid={`admin-phone-reject-${p.id}`}>Refuser</Button>
              </div>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="claimed" className="mt-4 space-y-2">
          {claimed.length === 0 && <p className="text-sm text-slate-500">Aucune fiche revendiquée.</p>}
          {claimed.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-claimed-${b.id}`}>
              <div>
                <Link to={`/entreprise/${b.id}`} className="font-semibold hover:text-[#1E40AF]">{b.name}</Link>
                <p className="font-mono-id text-xs text-slate-500">SIRET {b.siret} · {b.owner_email} · {b.plan_active ? `Formule ${b.plan_raw}` : "Sans abonnement"}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => act(() => api.post(`/admin/businesses/${b.id}/unclaim`), "Revendication retirée")} data-testid={`admin-unclaim-${b.id}`}>Retirer la revendication</Button>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="reviews" className="mt-4 space-y-2">
          {reviews.length === 0 && <p className="text-sm text-slate-500">Aucun avis.</p>}
          {reviews.map((r) => (
            <div key={r.id} className={`flex flex-wrap items-start justify-between gap-3 rounded-xl border bg-white p-4 ${r.reported ? "border-red-300" : "border-slate-200"}`} data-testid={`admin-review-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">{r.author}<Stars value={r.rating} />
                  {r.verified && <span className="rounded-full bg-[#DCFCE7] px-2 py-0.5 text-xs text-[#14532D]">Vérifié</span>}
                  {r.reported && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700" data-testid={`admin-reported-badge-${r.id}`}>Signalé par le pro</span>}
                </p>
                <Link to={`/entreprise/${r.business_id}`} className="text-xs text-[#1E40AF]">Voir l'entreprise</Link>
                <p className="mt-1 text-sm text-slate-700">{r.comment}</p>
                {r.reported && <p className="mt-2 rounded-lg bg-red-50 p-2 text-sm text-red-800" data-testid={`admin-report-reason-${r.id}`}>Motif : {r.report_reason}</p>}
              </div>
              <div className="flex gap-2">
                {r.reported && <Button variant="outline" size="sm" onClick={() => act(() => api.post(`/admin/reviews/${r.id}/dismiss`), "Signalement ignoré")} data-testid={`admin-dismiss-report-${r.id}`}>Conserver l'avis</Button>}
                <Button variant="destructive" size="sm" onClick={() => act(() => api.delete(`/admin/reviews/${r.id}`), "Avis supprimé")} data-testid={`admin-delete-review-${r.id}`}>Supprimer</Button>
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
